import type { Bot } from "@dante/contracts";
import { Button } from "@dante/ui-web";
import { useLingui } from "@lingui/react/macro";
import { Bell, PanelLeftClose, PanelLeftOpen, Plus } from "lucide-react";
import { useEffect } from "react";
import { IconTooltip } from "../../components/IconTooltip";
import { desktopBridge } from "../../lib/desktop";
import type { Panel } from "../../lib/right-panel-state";
import { WindowChrome } from "../WindowChrome";
import { isCreateMenuHotkey } from "./command-palette-hotkey";

/**
 * Header data the shell still hands over while per-bot chrome is being removed. A Space answers
 * in one unified chat, so the header creates nothing but spaces and reads none of these.
 */
type LegacyHeaderProps = {
  bots?: Bot[];
  setMobileSidebarOpen?: (open: boolean) => void;
  setPanel?: (panel: Panel) => void;
  navigate?: (path: string) => void;
  setCreateBotOpen?: (open: boolean) => void;
  setPickerInfoTopic?: (topic: "group" | "space") => void;
};

export type SidebarHeaderProps = {
  mini?: boolean;
  activityMode: boolean;
  toggleActivityMode: () => void;
  setBotsSidebarCollapsedPref: (collapsed: boolean) => void;
  setNewSpaceOpen: (open: boolean) => void;
} & LegacyHeaderProps;

/** The sidebar's top row: window chrome, the runs view, the collapse toggle, and New space. */
export function SidebarHeader({
  mini = false,
  activityMode,
  toggleActivityMode,
  setBotsSidebarCollapsedPref,
  setNewSpaceOpen,
}: SidebarHeaderProps) {
  const { t } = useLingui();
  const hasWindowChrome = Boolean(desktopBridge());
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!isCreateMenuHotkey(event)) return;
      event.preventDefault();
      setNewSpaceOpen(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setNewSpaceOpen]);
  const toggleLabel = mini ? t`Show sidebar` : t`Minimize sidebar`;
  return (
    <div
      className={`app-drag flex items-center ${mini ? `flex-col gap-2 px-0 pb-2 ${hasWindowChrome ? "pt-4" : "pt-2"}` : "justify-between px-[18px] pb-3 pt-4"}`}
    >
      {!mini || hasWindowChrome ? <WindowChrome /> : null}
      <div className={`relative flex items-center ${mini ? "flex-col gap-0.5" : "gap-2.5"}`}>
        <IconTooltip label={t`Activity`}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Activity`}
            aria-pressed={activityMode}
            data-activity-mode={activityMode ? "on" : "off"}
            onClick={toggleActivityMode}
            className={`app-no-drag flex h-7 w-7 items-center justify-center rounded-full ${
              activityMode
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground/70 hover:text-foreground/75"
            }`}
          >
            <Bell
              size={15}
              strokeWidth={1.8}
              fill={activityMode ? "currentColor" : "none"}
              aria-hidden="true"
            />
          </Button>
        </IconTooltip>
        <IconTooltip label={toggleLabel}>
          <Button
            variant="ghost"
            size="icon-sm"
            className="app-no-drag hidden h-7 w-7 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground/75 md:inline-flex"
            aria-label={toggleLabel}
            data-testid={mini ? "restore-bots-sidebar" : "minimize-bots-sidebar"}
            onClick={() => setBotsSidebarCollapsedPref(!mini)}
          >
            {mini ? (
              <PanelLeftOpen size={15} strokeWidth={1.8} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={15} strokeWidth={1.8} aria-hidden="true" />
            )}
          </Button>
        </IconTooltip>
        <IconTooltip label={t`New space`}>
          <Button
            variant="ghost"
            size="icon-sm"
            className="app-no-drag flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground/75"
            aria-label={t`New space`}
            data-testid="new-space-trigger"
            onClick={() => setNewSpaceOpen(true)}
          >
            <Plus size={15} strokeWidth={1.8} aria-hidden="true" />
          </Button>
        </IconTooltip>
      </div>
    </div>
  );
}
